
import { BrandSystemConfig } from "../../../remotion/Components/Theme/Brand";
import { FieldContainer } from '../CaptionConfig/FieldContainer';
import { ColorCircles } from './ColorCircles/ColorCircles';
import { getEnumFromValue } from '../TemplateEditor';
import TrebbleColorPicker from "../../../../common/TrebbleColorPicker";
import TrebbleSelect from "../../../../common/TrebbleSelect";
import TrebbleCard from "../../../../common/TrebbleCard";
import ti18n from 'i18n!nls/ClipCreatori18n';

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
}

export const DEFAULT_COLOR_SETS = Object.freeze({
  TREBBLE: {
    label: "Trebble",
    value: "Trebble",
    primaryColor: "#e97d53",
    secondaryColor: "#ffe62c",
    accentColor: "#f0f0f0"
  },
  OCEAN: {
    label: "Ocean",
    value: "ocean",
    primaryColor: "#2563eb",
    secondaryColor: "#22d3ee",
    accentColor: "#052e7a"
  },
  PURPLE: {
    label: "Purple",
    value: "purple",
    primaryColor: "#7c3aed",
    secondaryColor: "#f0abfc",
    accentColor: "#2f0c4e"
  },
  FOREST: {
    label: "Forest",
    value: "forest",
    primaryColor: "#059669",
    secondaryColor: "#fbbf24",
    accentColor: "#047857"
  },
  CHERRY: {
    label: "Cherry",
    value: "cherry",
    primaryColor: "#dc2626",
    secondaryColor: "#6ee7b7",
    accentColor: "#991b1b"
  },
  CUSTOM: {
    label: "Custom",
    value: "custom",
    primaryColor: "#F2F2F2",
    secondaryColor: "#CCCCCC",
    accentColor: "#929292"
  },
});

const getColorSetLabelFromValues = (primaryColor: string, secondaryColor: string, accentColor: string) => {  
    const colorSet = Object.values(DEFAULT_COLOR_SETS).find(
        (colorSet) =>
            colorSet.primaryColor.toLowerCase() === primaryColor.toLowerCase() &&
            colorSet.secondaryColor.toLowerCase() === secondaryColor.toLowerCase() &&
            colorSet.accentColor.toLowerCase() === accentColor.toLowerCase()
    );
    return colorSet?.value || "custom";
};

interface ColorComponentProps {
    brandConfig: BrandSystemConfig;
    onBrandConfigChange: (brandConfig: BrandSystemConfig) => void;
}

export const ColorComponent = ({ brandConfig, onBrandConfigChange }: ColorComponentProps) => {
    const colorSetSelected = getColorSetLabelFromValues(
        brandConfig.primaryColor,
        brandConfig.secondaryColor,
        brandConfig.accentColor
    );

    const ColorOption = ({ color1, color2, accentColor, label }: { color1: string, color2: string, accentColor: string, label: string }) => {
        return (
            <div className="flex flex-row items-center">
                <div>
                    <ColorCircles colors={[color1, color2, accentColor]} size="w-4 h-4" />
                </div>
                <div className="opacity-50 px-2">{label}</div>
            </div>
        );
    };

    const ColorSetSelect = ({ onChange, value, className }: { onChange: (value: any) => void, value: any, className: string }) => {
        return (
            <TrebbleSelect
                value={value}
                className={className}
                options={Object.values(DEFAULT_COLOR_SETS)}
                onChange={onChange}
                optionRender={(option: { data: typeof DEFAULT_COLOR_SETS[keyof typeof DEFAULT_COLOR_SETS] }) => (
                    <ColorOption
                        color1={option.data.primaryColor}
                        color2={option.data.secondaryColor}
                        accentColor={option.data.accentColor}
                        label={option.data.label}
                    />
                )}
                labelRender={({ value }: { value: any }) => {
                    const selectedColorSet = getEnumFromValue(
                        value,
                        DEFAULT_COLOR_SETS
                    ) as any;

                    if (!selectedColorSet) return <span>Custom</span>;

                    return (
                        <ColorOption
                            color1={selectedColorSet.primaryColor}
                            color2={selectedColorSet.secondaryColor}
                            accentColor={selectedColorSet.accentColor}
                            label={selectedColorSet.label}
                        />
                    );
                }}
            />
        );
    };

    const onColorSetChange = (colorSetValue: any) => {
        const selectedColorSet = getEnumFromValue(
            colorSetValue,
            DEFAULT_COLOR_SETS
        ) as typeof DEFAULT_COLOR_SETS[keyof typeof DEFAULT_COLOR_SETS];
        
        onBrandConfigChange({
            ...brandConfig,
            primaryColor: selectedColorSet.primaryColor,
            secondaryColor: selectedColorSet.secondaryColor,
            accentColor: selectedColorSet.accentColor
        });
    };

    const CustomColorSetConfig = () => {
        return (
            <div className="flex flex-col">
                <FieldContainer 
                    label="Primary color"
                    description={window.getI18n(ti18n, "PRIMARY_COLOR_DESCRIPTION")}
                >
                    <TrebbleColorPicker
                        value={brandConfig.primaryColor}
                        onChangeComplete={(primaryColor) => onBrandConfigChange({
                            ...brandConfig,
                            primaryColor: primaryColor.toHexString()
                        })}
                    />
                </FieldContainer>

                <FieldContainer 
                    label="Secondary color"
                    description={window.getI18n(ti18n, "SECONDARY_COLOR_DESCRIPTION")}
                >
                    <TrebbleColorPicker
                        value={brandConfig.secondaryColor}
                        onChangeComplete={(secondaryColor) => onBrandConfigChange({
                            ...brandConfig,
                            secondaryColor: secondaryColor.toHexString()
                        })}
                    />
                </FieldContainer>

                <FieldContainer 
                    label="Accent color"
                    description={window.getI18n(ti18n, "ACCENT_COLOR_DESCRIPTION")}
                >
                    <TrebbleColorPicker
                        value={brandConfig.accentColor}
                        onChangeComplete={(accentColor) => onBrandConfigChange({
                            ...brandConfig,
                            accentColor: accentColor.toHexString()
                        })}
                    />
                </FieldContainer>
            </div>
        );
    };

    const CompactColorSetConfig = () => {
        return (
            <div>
                <div className="mb-2">Custom colors</div>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col items-center">
                        <TrebbleColorPicker
                            value={brandConfig.primaryColor}
                            onChangeComplete={(primaryColor) => onBrandConfigChange({
                                ...brandConfig,
                                primaryColor: primaryColor.toHexString()
                            })}
                            showText={false}
                        />
                        <span className="mt-1 opacity-50">Primary</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <TrebbleColorPicker
                            value={brandConfig.secondaryColor}
                            onChangeComplete={(secondaryColor) => onBrandConfigChange({
                                ...brandConfig,
                                secondaryColor: secondaryColor.toHexString()
                            })}
                            showText={false}
                        />
                        <span className="mt-1 opacity-50">Secondary</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <TrebbleColorPicker
                            value={brandConfig.accentColor}
                            onChangeComplete={(accentColor) => onBrandConfigChange({
                                ...brandConfig,
                                accentColor: accentColor.toHexString()
                            })}
                            showText={false}
                        />
                        <span className="mt-1 opacity-50">Accent</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
            <>
                <FieldContainer 
                    label="Color set"
                    description={window.getI18n(ti18n, "COLOR_SET_DESCRIPTION")}
                >
                    <ColorSetSelect
                        className="w-full"
                        onChange={onColorSetChange}
                        value={colorSetSelected}
                    />
                </FieldContainer>

                {colorSetSelected === DEFAULT_COLOR_SETS.CUSTOM.value && (
                    <CompactColorSetConfig />
                )}
        </>
    );
};